<template>
  <transition name="fade">
    <div v-if="show" class="modal__cover" :style="style" @click.stop>
      <div
        class="modal__container"
        :style="{ transform: `scale(${scale * 1}) translate3d(0, -50%, 0)` }"
        :data-type="layout"
        :data-size="size"
      >
        <div class="modal__inner">
          <div class="modal__header">
            <!-- single: 单标题 -->
            <template v-if="layout === 'single'">
              <div class="title" @click="$emit('clickTitle')">
                <div class="bg">
                </div>
                <div v-if="iconType" class="icon" :class="iconType"></div>
                <div class="label-box">
                  <span class="label-shadow">{{ title }}</span>
                  <span class="label-text">{{ title }}</span>
                </div>
              </div>
            </template>
            <!-- multi: 多 tab -->
            <template v-else>
              <div class="tabs-group">
                <div
                  v-for="(tab, index) in tabs"
                  :key="index"
                  :class="['tab', { active: activeTab === index }, tab.iconType]"
                  @click="activeTab = index"
                >
                  <div class="bg"></div>
                  <div class="icon"></div>
                  <div class="label-box">
                    <span class="label-shadow">{{ tab.name }}</span>
                    <span class="label-text">{{ tab.name }}</span>
                  </div>
                </div>
              </div>
            </template>
            <div v-if="decorate" class="decorate"></div>
            <div class="modal__header-right">
              <slot name="header-right"></slot>
            </div>
          </div>
          <div class="modal__body" :class="{ 'is-empty': empty }">
            <!-- 底部装饰条 (bg_popup_bottom, 158px 高), 由 prop 控制 -->
            <div v-if="bottomBg" class="modal__bottom-bg"></div>
            <slot v-if="layout === 'single'"></slot>
            <component :is="tabs[activeTab].component" v-else-if="tabs.length" />
          </div>
        </div>
        <div class="close" @click="$emit('close')"></div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useAppStore } from '@/store'

defineOptions({ name: 'UiModal' })

defineEmits(['close', 'clickTitle'])

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  // 'single' 单标题 | 'multi' 多 tab 切换 (后者需配合 tabs)
  layout: {
    type: String,
    default: 'single',
    validator: (v) => ['single', 'multi'].includes(v)
  },
  // 'default' 常规粉底弹窗 | 'lg' 整图 container 大弹窗 (弹窗自定义 container 切图, 内部 header title/body 粉底自动隐藏)
  size: {
    type: String,
    default: 'default',
    validator: (v) => ['default', 'lg'].includes(v)
  },
  title: {
    type: String,
    default: '弹窗标题'
  },
  iconType: {
    type: String,
    default: ''
  },
  decorate: {
    type: Boolean,
    default: false
  },
  // 内容为空态时切到 $bg_popup_inner_empty 底图
  empty: {
    type: Boolean,
    default: false
  },
  // 显示底部装饰条 (bg_popup_bottom, 158px 高); 原 .popup-bottom 三处使用点统一进来
  bottomBg: {
    type: Boolean,
    default: false
  },
  // layout=multi 时使用, 每项需含 name / component (可选 iconType)
  tabs: {
    type: Array,
    default: () => [],
    validator: (tabs) => tabs.every((t) => 'name' in t && 'component' in t)
  }
})

const appStore = useAppStore()

const activeTab = ref(0)
const style = ref({})

const scale = computed(() => appStore.scale)
const clientSize = computed(() => appStore.clientSize)

watch(
  () => props.show,
  (newVal) => {
    if (newVal) {
      activeTab.value = 0
      resetHeight()
    }
  }
)

watch(
  clientSize,
  (newVal, oldVal) => {
    if (newVal !== oldVal && props.show) {
      resetHeight()
    }
  },
  { deep: true }
)

function resetHeight() {
  style.value = { height: `${window.innerHeight + 1}px` }
}
</script>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.1s linear;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.modal__cover {
  width: 100%;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.8);
  position: fixed;
  top: 0;
  left: 0;
  z-index: 60;

  .modal__container {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 1096px;
    height: 644px;
    background-image: $bg_popup;
    margin-left: -0.5 * 1096px;
    transform-origin: center 0;
    transform: translate3d(0, -50%, 0);
    display: flex;

    // close 提到 modal__container 顶层, 相对整个弹窗外框定位 (不再受 header 宽度影响)
    > .close {
      position: absolute;
      width: 32px;
      height: 32px;
      top: 1px;
      right: 3px;
      background: $icon_close center/contain no-repeat;
      cursor: pointer;
      -webkit-tap-highlight-color: transparent;
      z-index: 2;
    }

    .decorate {
      width: 83px;
      height: 84px;
      background-image: $bg_decorate;
      background-size: 1472px 639px;
      background-position: -1345px -229px;
      background-repeat: no-repeat;
      position: absolute;
      top: 8px;
      right: 20px;
    }

    // ===== 通用 =====
    .modal__inner {
      display: flex;
      flex-direction: column;
      width: 1006px;
      height: calc(542px + 67px);
      margin: 27px auto 0;
      position: relative;
    }

    .modal__header {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: -5px;
      z-index: 1;

      .modal__header-right {
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: flex-end;
      }
    }

    .modal__body {
      width: 1006px;
      height: 542px;
      display: flex;
      align-items: center;
      flex-direction: column;
      position: relative;
      background: $bg_popup_inner center/contain no-repeat;
      margin-left: 7.5px;

      &.is-empty {
        background-image: $bg_popup_inner_empty;
      }

      // 底部装饰条 (bottomBg=true 时启用), 158px 高绝对定位吸底
      .modal__bottom-bg {
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 158px;
        background: $bg_popup_bottom center bottom/cover no-repeat;
        overflow: hidden;
        pointer-events: none;
      }
    }

    // size="lg" 大弹窗变体: container 换整图切图 + 内部布局微调
    &[data-size="lg"] {
      background-image: url(https://website.xdcdn.net/poster/58881/26match/common/QW8zuuas.png);
      .modal__inner {
        width: 100%;
        height: 100%;
        margin: 0;
      }
      .modal__header {
        margin: 24px 18px;
        .title > .bg {
          display: none;
        }
      }
      .modal__body {
        position: absolute;
        left: 0;
        top: 0;
        background: none;
        margin: 0;
        width: 100%;
        height: 100%;
      }
      > .close {
        top: 25px;
        right: 12px;
      }
    }

    // .title (single) 和 .tab (multi) 共享内部布局: 居中 flex + .bg/.icon/.label-box 三件套
    .title,
    .tab {
      position: relative;
      z-index: 1;
      display: flex;
      align-items: center;
      justify-content: center;

      .bg {
        position: absolute;
        inset: 0;
      }
      .icon,
      .label-box {
        position: relative;
        z-index: 1;
      }
      .icon {
        width: 36px;
        height: 36px;
      }
      .label-box {
        font-size: 26px;
        font-weight: 500;
        .label-shadow { color: #FFFFFF; }
        .label-text   { color: #761114; }
      }
    }

    // ============ single: 单标题弹窗 ============
    &[data-type='single'] {
      .modal__header { height: 67px; }

      .title {
        width: 277px;
        height: 67px;
        padding-right: 40px;
        box-sizing: border-box;

        .bg {
          background: url(https://website.xdcdn.net/poster/58881/26match/common/P8mbVxl6.png) center/contain no-repeat;
          left: -2px;
        }
        .icon {
          margin-right: 7px;
          margin-top: 10px;

          // 自动展开 icon_singles / icon_sprites 里所有 key, 消费端 iconType 直接传 key 即可
          @each $name, $url in $icon_singles {
            &.#{$name} { @include icon_status('#{$name}'); }
          }
          @each $name, $url in $icon_sprites {
            &.#{$name}_1 { @include icon_status('#{$name}_1'); }
            &.#{$name}_2 { @include icon_status('#{$name}_2'); }
            &.#{$name}_3 { @include icon_status('#{$name}_3'); }
          }

        }
        .label-box { margin-top: 10px; }
      }
    }

    // ============ multi: 多 tab 切换弹窗 ============
    // header 纯 CSS 平行四边形, 自动适配任意数量 tab, 不依赖切图
    &[data-type='multi'] {
      // tab 视觉变量 — 一处改全部跟着改
      $tab-skew: 12deg;
      $tab-bg-default: #B5B5B5;
      $tab-bg-active: #DEDEDE;

      .modal__header {
        margin-top: calc(67px - 54px);
        margin-left: 11px;
        height: 54px;
      }

      // tabs-group: 多 tab 的 wrapper, 包含整体平行四边形灰底, 范围严格限定不延伸到 header-right
      .tabs-group {
        height: 100%;
        display: flex;
        position: relative;
        box-sizing: border-box;
        padding-top: 4px;

        &::before {
          content: '';
          position: absolute;
          inset: 0;
          transform: skewX($tab-skew);
          background: $tab-bg-default;
          border-top: #F7F7F7 4px solid;
          box-sizing: border-box;
          z-index: 0;
        }
      }

      .tab {
        height: 100%;
        width: 220px;
        cursor: pointer;
        -webkit-tap-highlight-color: transparent;

        // 平行四边形 bg, 默认透明, 激活时显示浅灰
        .bg {
          transform: skewX($tab-skew);
          background: transparent;
          transition: background 0.15s;
        }
        .icon { margin-right: 2px; }

        &.prize .icon { @include icon_status('reward_1'); }
        &.rule  .icon { @include icon_status('rule_1'); }

        &.active .bg { background: $tab-bg-active; }
      }
    }
  }
}
</style>
